import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Box, Separator, Text, VStack } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useInitWithdrawProposal } from 'hooks';
import useInputAttributes from 'hooks/useInputAttributes';
import { useTranslation } from 'localization';
import { routes } from 'statics';
import { LDUser, LDUsers } from 'types';

import { Button, Input, PayWithOption, PayWithSelect } from 'components';
import { yup } from 'utils';

type PayloadType = {
  amount: number;
  pay_with: string;
  note: string;
};

type InitFormProps = {
  currencyList: PayWithOption[];
  ldUsers: LDUsers;
  walletId: string;
  activeLD: null | LDUser;
};

const InitForm: React.FC<InitFormProps> = ({
  currencyList,
  ldUsers,
  walletId,
  activeLD,
}: InitFormProps) => {
  const { t } = useTranslation('component.withdraw.ld');
  const navigate = useNavigate();

  const { mutateAsync: submit, isPending: submitLoading } =
    useInitWithdrawProposal();

  const onSubmit = async (data: PayloadType) => {
    if (ldUsers) {
      submit({
        ...data,
        ld_identifier_number: activeLD ? activeLD.identifier_number : 0,
        source_wallet: walletId,
        transaction_method_category: 'WITHDRAW_LD',
      }).then((res) => {
        if (res.id) {
          navigate(`${routes.wallets.url}?tab=withdraw`);
        }
      });
    }
  };

  const schema = yup.object().shape({
    pay_with: yup.string().label(t('form.pay_with')).required(),
    amount: yup.number().label(t('form.amount')).required(),
    note: yup.string().label(t('form.note')).required(),
  });

  const {
    register,
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {},
  });

  const submitForm = async (data: PayloadType) => {
    onSubmit(data).then(() => {
      reset();
    });
  };

  const formProps = useInputAttributes(register, errors, {
    amount: t('form.amount'),
    note: t('form.note'),
  });

  React.useEffect(() => {
    if (currencyList && currencyList.length > 0) {
      setValue('pay_with', currencyList[0].value);
    }
  }, [currencyList, setValue]);

  const surfaceFieldProps = {
    background: 'secondary',
    borderWidth: '1px',
    borderColor: 'rgba(255,255,255,0.08)',
    borderRadius: 'lg',
    _hover: { background: 'rgba(255,255,255,0.05)' },
    _focus: {
      background: 'secondary',
      borderColor: 'primary',
    },
  };

  return (
    <form onSubmit={handleSubmit(submitForm)}>
      <VStack w="100%" align="stretch" gap={5}>
        <Controller
          name="pay_with"
          control={control}
          render={({ field }) => (
            <PayWithSelect
              label={t('form.pay_with')}
              error={errors.pay_with?.message}
              options={currencyList}
              value={field.value || ''}
              onChange={field.onChange}
              placeholder={t('form.pay_with')}
            />
          )}
        />
        <Input
          {...formProps.note}
          isTextArea
          maxH={200}
          {...surfaceFieldProps}
        />
        <Separator borderColor="rgba(255,255,255,0.08)" />
        <VStack w="100%" align="stretch" gap={3}>
          <Text w="100%" textAlign="left" color="body2" fontWeight="medium">
            {t('enterAmount')}
          </Text>
          <Box w="100%" pos="relative">
            <Input
              {...formProps.amount}
              label=""
              type="number"
              min="0"
              step="0.01"
              {...surfaceFieldProps}
              h="auto"
              minH="3.5rem"
              py={4}
              px={4}
              fontSize="2xl"
              fontWeight="500"
              color="body"
              _placeholder={{ color: 'body2' }}
            />
            <Text fontSize="sm" pos="absolute" top={3} right={3} color="body2">
              {watch('pay_with') || ''}
            </Text>
          </Box>
        </VStack>
        <Button
          type="submit"
          variant="filled"
          display="block"
          title={t('confirm')}
          minW="100%"
          mt={1}
          isLoading={submitLoading}
        />
      </VStack>
    </form>
  );
};
export default InitForm;
